import type {
  Tunnus,
  TunnusBroadcastMessageVariant,
  UserInfo,
} from '@yleisradio/tunnus-sdk';
import { useAnalyticsAvailability } from 'contexts/AnalyticsAvailabilityContext';
import { useUILanguage } from 'hooks/useUILanguage';
import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';
import logger from 'services/logger';
import { isTestEnvironment } from 'utils/environment';
import { IdTokenRefresher } from './IdTokenRefresher';

const tunnusSdkPromise = import('@yleisradio/tunnus-sdk');

type Value = {
  isLoggedIn: boolean;
  isUserInformationFetched: boolean;
  user: UserInfo | undefined;
  yleTunnusInstance: Tunnus | undefined;
};

const TunnusContext = createContext<Value>({
  isLoggedIn: false,
  isUserInformationFetched: false,
  user: undefined,
  yleTunnusInstance: undefined,
});

type Props = {
  children: React.ReactNode;
  hasTunnusSessionCookie: boolean;
};

export const TunnusContextProvider: React.FunctionComponent<Props> = ({
  children,
  hasTunnusSessionCookie,
}) => {
  const [yleTunnusInstance, setYleTunnusInstance] = useState<Tunnus>();
  const [isUserInformationFetched, setIsUserInformationFetched] =
    useState<boolean>(false);
  const [userInformation, setUserInformation] = useState<UserInfo>();
  const isLoggedIn = isUserInformationFetched
    ? !!userInformation
    : hasTunnusSessionCookie;
  const isSessionValidated = isLoggedIn && isUserInformationFetched;

  const language = useUILanguage();
  const { isAvailable: isAnalyticsAvailable, hasErrored: hasAnalyticsErrored } =
    useAnalyticsAvailability();
  const router = useRouter();
  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (isAnalyticsAvailable || hasAnalyticsErrored) {
      (async function () {
        try {
          const { default: createTunnus } = await tunnusSdkPromise;

          const tunnus = createTunnus({
            initiatingApp: 'areena-web-items',
            language: language,
            returnToUrl: window.location.href,
            environment: isTestEnvironment() ? 'test' : 'production',
            theme: 'dark',
            openingEvent: 'defaultWithFocus',
          });

          setYleTunnusInstance(tunnus);

          const user = await tunnus.getUserInfo();
          setIsUserInformationFetched(true);
          if (user) {
            setUserInformation(user);
          }
        } catch (e) {
          logger.error(e, 'Error initializing Yle Tunnus SDK');
        }
      })();
    }
  }, [hasAnalyticsErrored, isAnalyticsAvailable, language]);

  useEffect(() => {
    const tunnusChangesChannel = new BroadcastChannel('yle-tunnus');

    const listener = ({
      data: message,
    }: MessageEvent<TunnusBroadcastMessageVariant>) => {
      if (message.type === 'tunnus-user-info-changed') {
        setIsUserInformationFetched(true);
        setUserInformation(message.user);
      } else if (message.type === 'tunnus-logged-in') {
        setIsUserInformationFetched(true);
        setUserInformation(message.user);

        // Profile requests are authenticated by the yleidtoken cookie. On a user
        // switch that token lags behind, so refresh it for the new user *before*
        // refetching — otherwise personalized lists return the previous user's data
        // (leak) or 400 mid-switch. Only then force the refetch.
        void (async () => {
          try {
            await yleTunnusInstance?.getIdToken();
          } catch (e) {
            logger.error(e, 'Error refreshing ID token after login');
          }
          mutate(() => true, undefined, { revalidate: true });
          router.replace(router.asPath, undefined, { scroll: false });
        })();
      } else if (message.type === 'tunnus-logged-out') {
        setUserInformation(undefined);

        // Drop cached data without refetching: a logged-out session must not fire
        // authenticated requests (they fail with 400/401)
        mutate(() => true, undefined, { revalidate: false });
        router.replace(router.asPath, undefined, { scroll: false });
      }
    };

    tunnusChangesChannel.addEventListener('message', listener);

    return () => {
      tunnusChangesChannel.removeEventListener('message', listener);
      tunnusChangesChannel.close();
    };
  }, [mutate, router, yleTunnusInstance]);

  const value = useMemo(
    (): Value => ({
      isLoggedIn,
      isUserInformationFetched,
      user: userInformation,
      yleTunnusInstance,
    }),
    [isLoggedIn, isUserInformationFetched, userInformation, yleTunnusInstance]
  );

  return (
    <TunnusContext.Provider value={value}>
      {!!yleTunnusInstance && isSessionValidated && (
        <IdTokenRefresher yleTunnusInstance={yleTunnusInstance} />
      )}

      {children}
    </TunnusContext.Provider>
  );
};

export function useTunnusContext(): Value {
  return useContext(TunnusContext);
}
